import styled, { css } from "styled-components";

// Common button base styles
const BaseButton = css`
  border: 1px solid var(--border-color);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 400;
  
  &:hover {
    opacity: 0.85;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Common input base styles
const BaseInput = css`
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--input-background);
  color: var(--text-primary);
  font-size: 12px;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: var(--admin-tab-bg);
  }
`;

// Common clear button styles (reusable for X buttons)
const BaseClearButton = css`
  background: transparent;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: var(--text-primary);
  }

  &:focus {
    outline: none;
  }
`;

export const AllSiteSettingsContainer = styled.div`
  padding: 22px 30px;
  background-color: var(--section-background);
  max-height: calc(100vh - 60px);
  overflow: auto;
  
  @media (max-width: 850px) {
    padding: 13px;
    max-height: none;  
    overflow: visible; 
    min-height: 100vh; 
  }
`;

export const Button = styled.button`
  ${BaseButton}
  padding: 8px 16px;
  background: var(--admin-tab-bg);
  color: var(--theme-text);
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;

  &:active {
    opacity: 0.8;
  }
`;

export const Heading = styled.div`
  h3 {
    font-weight: 700;
    font-size: 16px;
    line-height: 31px;
    color: var(--text-primary);
    margin: 0;
  }
`;

export const WorkSection = styled.div`
  padding: 24px 28px;
  background: var(--inner-section-bg);
`;

export const ContentArea = styled.div`
  background: var(--input-background);
  padding: 20px;
  border-radius: 8px;
  box-shadow: 2px 4px 5px 2px rgba(0, 0, 0, 0.02);
  margin-top: 20px;

  h2 {
    margin: 0 0 20px 0;
    font-size: 18px;
    font-weight: 700;
    color: var(--theme-text);
    font-family: "Lato";
  }

  p {
    font-size: 14px;
    color: var(--theme-text);
    font-family: "Lato";
  }
`;

export const BlueAccordionStyled = styled.div`
  > .MuiPaper-root {
    box-shadow: none;
    margin-bottom: 15px;
    
    > .MuiAccordionSummary-root {
      padding: 0 28px;
      background: var(--admin-tab-bg);
      border: 1px solid var(--border-color);
      
      .MuiAccordionSummary-expandIconWrapper {
        color: var(--text-primary);
      }
    }
    
    .MuiAccordionDetails-root {
      padding: 0;
    }

    .MuiAccordionSummary-content {
      margin: 13px 0;
      h3 {
        font-weight: 700;
        font-size: 16px;
        line-height: 31px;
        color: var(--text-primary);
      }
    }
    
    .MuiAccordionSummary-root.Mui-expanded {
      min-height: 60px;
    }
    
    .MuiAccordionSummary-content.Mui-expanded {
      margin: 13px 0;
    }
  }
`;

export const FilterButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  align-items: center;
`;

export const FilterButton = styled.button`
  ${BaseButton}
  background: var(--admin-tab-bg);
  color: ${props => props.active ? 'var(--theme-text)' : 'var(--text-secondary)'};
  min-height: 28px;
  padding: 0 12px;
  font-size: 12px;
  line-height: 150.5%;
  text-transform: initial;
  white-space: nowrap;
`;

export const DeleteToggleButton = styled.button`
  ${BaseButton}
  background: var(--admin-tab-bg);
  color: ${props => props.active ? 'var(--theme-text)' : 'var(--text-secondary)'};
  min-height: 28px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  white-space: nowrap;

  svg {
    width: 14px;
    height: 14px;
    color: ${props => props.active ? 'var(--theme-text)' : 'var(--text-secondary)'};
  }
`;

export const SearchFilterContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

export const SearchInput = styled.input`
  ${BaseInput}
  min-height: 28px;
  padding: 0 28px 0 10px;
  width: 140px;

  &::placeholder {
    color: var(--placeholder-text-color);
    font-size: 11px;
  }

  &.name-search {
    width: 130px;
  }

  &.pmid-search {
    width: 100px;
  }
`;

export const ClearButton = styled.button`
  ${BaseClearButton}
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  font-weight: bold;
  line-height: 1;
  z-index: 1;
`;

export const ProductLevelDropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

export const ProductLevelButton = styled.button`
  ${BaseButton}
  background: var(--input-background);
  color: var(--text-primary);
  min-height: 28px;
  padding: 0 ${props => props.hasSelection ? '56px' : '28px'} 0 10px;
  font-size: 12px;
  min-width: 140px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  white-space: nowrap;

  &.product-level-select {
    min-width: 125px;
  }

  &.timezone-select {
    min-width: 115px;
  }

  &:hover {
    border-color: var(--admin-tab-bg);
  }

  &::after {
    content: '▼';
    position: absolute;
    right: 8px;
    font-size: 9px;
    color: var(--text-secondary);
  }
`;

export const ProductLevelDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: var(--input-background);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 220px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  padding: 8px 0;
`;

export const ProductLevelOption = styled.label`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 12px;
  color: var(--text-primary);
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--section-background);
  }

  input[type="checkbox"] {
    margin-right: 8px;
    cursor: pointer;
  }
`;

export const ClearAllButton = styled.button`
  ${BaseButton}
  width: 100%;
  padding: 8px 16px;
  border-top: 1px solid var(--section-border-color);
  background: var(--section-background);
  color: var(--text-secondary);
  font-size: 13px;
  text-align: left;
  margin-top: 4px;

  &:hover {
    background: var(--inner-section-bg);
    color: var(--text-primary);
  }
`;

export const ProductLevelClearButton = styled.button`
  ${BaseClearButton}
  position: absolute;
  right: 26px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  font-weight: bold;
  line-height: 1;
  z-index: 1;
`;

export const EditButton = styled.button`
  ${BaseButton}
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--admin-tab-bg);
  padding: 0;
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    color: var(--theme-text);
    font-size: 18px;
  }
`;

export const ModalBox = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  background: var(--input-background);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: auto;
`;

export const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--header-bg);
  
  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: var(--theme-text);
  }
`;

export const ModalContent = styled.div`
  padding: 24px;
`;

export const ModalSection = styled.div`
  margin-bottom: 32px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--admin-tab-bg);
`;

export const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

export const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
`;

export const InfoRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  
  strong {
    color: var(--text-primary);
    font-weight: 600;
  }
  
  span {
    color: var(--text-secondary);
  }
`;

export const NumberInput = styled.input`
  ${BaseInput}
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
`;

export const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: var(--header-bg);
`;

export const ModalButton = styled.button`
  ${BaseButton}
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 500;
  
  &.primary {
    background: var(--admin-tab-bg);
    color: var(--theme-text);
    
    &:hover {
      opacity: 0.85;
    }
  }
  
  &.secondary {
    background: var(--text-secondary);
    color: var(--theme-text);
    
    &:hover {
      opacity: 0.85;
    }
  }
`;
